import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import Layout from './components/layout/Layout';
import SetupDashboard from './pages/SetupDashboard';
import CalculatorPage from './pages/CalculatorPage';
import ServiceEditor from './pages/ServiceEditor'; 
import ServiceCostCalculator from './pages/ServiceCostCalculator';
import BusinessSettings from './pages/BusinessSettings';
import StaffCards from './pages/StaffCards';
import CalculationHistory from './pages/CalculationHistory';
import AdminDashboard from './pages/AdminDashboard';
import ServiceImport from './components/ServiceImport';
import ProductImport from './components/ProductImport';
import './index.css';
import { Toaster } from 'react-hot-toast';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsAndConditions from './pages/TermsAndConditions';
import Dashboard from './pages/Dashboard';

// Auth pages - local JWT authentication
import LandingPage from './pages/LandingPage';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';

// Subscription pages
import Subscription from './pages/Subscription';
import SubscriptionStatus from './pages/SubscriptionStatus';

// Import Material UI ThemeProvider
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';

const theme = createTheme({
  palette: {
    primary: {
      main: '#556cd6',
    },
    secondary: {
      main: '#19857b',
    },
    background: {
      default: '#fff',
    },
  },
});

function App() {
  console.log('SalonLogic Pricing Calculator - Local JWT Auth v4.0 - Build: ' + Date.now());
  
  return (
    <>
      <Toaster position="top-center" reverseOrder={false} /> 
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <AuthProvider>
          <Router>
            <Layout>
              <Routes>
                {/* Public landing page */}
                <Route path="/" element={<LandingPage />} />
                
                {/* Static pages */}
                <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                <Route path="/terms" element={<TermsAndConditions />} />
                
                {/* Auth routes - local JWT authentication */}
                <Route path="/login" element={<LandingPage />} />
                <Route path="/register" element={<Register />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                
                {/* Subscription routes */}
                <Route path="/subscribe" element={<Subscription />} />
                <Route path="/subscription/success" element={
                   <SubscriptionStatus />} />
                
                {/* Protected routes - require JWT authentication AND active subscription */}
                <Route path="/admin" element={
                  <ProtectedRoute adminRequired={true}>
                    <AdminDashboard />
                  </ProtectedRoute>
                } />
                
                <Route path="/dashboard" element={
                  <ProtectedRoute>
                    <Dashboard />
                  </ProtectedRoute>
                } />
                
                <Route path="/business-settings" element={
                  <ProtectedRoute>
                    <BusinessSettings />
                  </ProtectedRoute>
                } />
                
                <Route path="/staff-cards" element={
                  <ProtectedRoute>
                    <StaffCards />
                  </ProtectedRoute>
                } />
                
                {/* Service Import Route */}
                <Route path="/calculator/import" element={
                  <ProtectedRoute>
                    <ServiceImport />
                  </ProtectedRoute>
                } />
                
                <Route path="/calculator/import/products" element={
                  <ProtectedRoute>
                    <ProductImport />
                  </ProtectedRoute>
                } />
                
                {/* Calculator routes */}
                <Route path="/calculator/service-editor/new" element={
                  <ProtectedRoute>
                    <ServiceEditor />
                  </ProtectedRoute>
                } />
                
                <Route path="/calculator/service-editor/:serviceId" element={
                  <ProtectedRoute>
                    <ServiceEditor />
                  </ProtectedRoute>
                } />
                
                <Route path="/calculator/service/:serviceId/edit" element={
                  <ProtectedRoute>
                    <ServiceEditor />
                  </ProtectedRoute>
                } />
                
                <Route path="/calculator/service/:serviceId/calculate" element={
                  <ProtectedRoute>
                    <ServiceCostCalculator />
                  </ProtectedRoute>
                } />
                
                <Route path="/calculator/services" element={
                  <ProtectedRoute>
                    <CalculatorPage activeTab="services" />
                  </ProtectedRoute>
                } />
                
                <Route path="/calculator/products" element={
                  <ProtectedRoute>
                    <CalculatorPage activeTab="products" />
                  </ProtectedRoute>
                } />
                
                <Route path="/calculator/overheads" element={
                  <ProtectedRoute>
                    <CalculatorPage activeTab="overheads" />
                  </ProtectedRoute>
                } />
                
                <Route path="/calculator/staff" element={
                  <ProtectedRoute>
                    <CalculatorPage activeTab="staff" />
                  </ProtectedRoute>
                } />
                
                <Route path="/calculator/price-calculator" element={
                  <ProtectedRoute>
                    <CalculatorPage activeTab="calculator" />
                  </ProtectedRoute>
                } />
                
                <Route path="/calculator/settings" element={
                  <ProtectedRoute>
                    <CalculatorPage activeTab="settings" />
                  </ProtectedRoute>
                } />
                
                <Route path="/calculator/history" element={
                  <ProtectedRoute>
                    <CalculatorPage activeTab="history" />
                  </ProtectedRoute>
                } />
                
                <Route path="/calculator" element={
                  <ProtectedRoute>
                    <CalculatorPage />
                  </ProtectedRoute>
                } />
                
                <Route path="/history" element={
                  <ProtectedRoute>
                    <CalculationHistory />
                  </ProtectedRoute>
                } />
                
                {/* Catch all - redirect to landing page */}
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </Layout>
          </Router>
        </AuthProvider>
      </ThemeProvider>
    </>
  );
}

export default App;